import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import React, { useEffect, useState } from "react";
import Loader from "../Loader";
import { Button } from "../Button";
import User, { selectCurrentUser, selectUsersInProject } from "../../store/models/User";
import { InviteIcon } from "../Svg";
import { MemberItemModes, MemberItemsList } from "../MemberItem";
import { PendingInviteItemsList } from "../PendingInviteItem";
import { ModalTypes, UserRoleIds } from "../../constants";
import UIState from "../../store/UIState";
import { toast } from "react-toastify";
import _ from "lodash";

const selector = createSelector(
  selectCurrentUser,
  (state, projectId) => selectUsersInProject(state, projectId),
  (currentUser, usersInProject) => {
    return {
      currentUser,
      activeUsersInProject: usersInProject.filter(u => !u.disabled),
      disabledUsersInProject: usersInProject.filter(u => u.disabled),
    }
  }
)

export const MiniProjectRepsTab = ({ projectId }) => {
  const dispatch = useDispatch();
  const {
    currentUser,
    activeUsersInProject,
    disabledUsersInProject
  } = useSelector(state => selector(state, projectId));

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pendingInvites, setPendingInvites] = useState([])
  const currentUserIsAdmin = currentUser?.role === UserRoleIds.ADMIN;
  const currentUserIsPartner = currentUser?.role === UserRoleIds.PARTNER;
  // In mini dashboard, partners should be able to invite
  const canInvite = currentUserIsAdmin || currentUserIsPartner;

  const fetchTeamMembers = async () => {
    console.log('=== fetchTeamMembers called ===');
    console.log('Current user before fetch:', currentUser);
    console.log('Current user role:', currentUser?.role);
    console.log('ProjectId:', projectId);
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch project users to see what data comes back
      console.log('Calling fetchProjectUsers API...');
      const result = await dispatch(User.actions.fetchProjectUsers(projectId));
      console.log('=== API Response ===');
      console.log('Result:', result);
      console.log('Number of users returned:', result?.length);
      
      // Log each user to see if current user data is affected
      if (result && result.length > 0) {
        console.log('=== All Users Returned ===');
        result.forEach((user, index) => {
          console.log(`User ${index + 1}:`, {
            id: user.id,
            email: user.email,
            role: user.role,
            disabled: user.disabled,
            first_name: user.first_name,
            last_name: user.last_name
          });
        });
        
        // Check if current user is in the list
        const currentUserInList = result.find(u => u.id === currentUser?.id);
        if (currentUserInList) {
          console.log('=== Current User Found in List ===');
          console.log('Current user data from API:', currentUserInList);
          console.log('Current user role from API:', currentUserInList.role);
          console.log('Current user role in state:', currentUser?.role);
          console.log('Role changed?', currentUserInList.role !== currentUser?.role);
        }
      }
      
      console.log('Active users in project:', activeUsersInProject.length);
      console.log('Disabled users in project:', disabledUsersInProject.length);
      
      // Fetch pending invites
      try {
        console.log('Fetching pending invites...');
        const invites = await dispatch(User.actions.fetchPendingInviteesForProject(projectId));
        console.log('Pending invites:', invites);
        setPendingInvites(invites || []);
      } catch (e) {
        console.warn('Could not fetch pending invites:', e);
        setPendingInvites([]);
      }
    } catch (e) {
      console.error('=== Error in fetchTeamMembers ===', e);
      setError(e.message || 'Failed to load team members')
    } finally {
      setLoading(false);
      console.log('Current user after fetch:', currentUser);
      console.log('Current user role after fetch:', currentUser?.role);
    }
  }

  // Auto-fetch on mount
  useEffect(() => {
    if (!projectId) return;
    fetchTeamMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const onInviteUser = () => {
    dispatch(UIState.actions.showModal(ModalTypes.INVITE_TEAM_MEMBERS_TO_PROJECT, {
      projectId,
      // Only allow inviting users as Representative or Partner
      allowedRoles: [UserRoleIds.REPRESENTATIVE, UserRoleIds.PARTNER],
      onDone: async () => {
        // Refetch project users after inviting, but handle errors gracefully
        try {
          await dispatch(User.actions.fetchProjectUsers(projectId));
        } catch (e) {
          console.error('Error refreshing project users:', e);
          toast.error('Could not refresh user list');
        }
        
        // Refresh pending invites
        try {
          const invites = await dispatch(User.actions.fetchPendingInviteesForProject(projectId));
          setPendingInvites(invites || []);
        } catch (e) {
          console.warn('Could not refresh pending invites:', e);
        }
      }
    }))
  }

  const onRemoveUser = async (user) => {
    console.log('=== onRemoveUser called ===');
    console.log('User:', user);
    console.log('User ID:', user.id);
    console.log('Project ID:', projectId);
    console.log('User disabled status:', user.disabled);
    
    try {
      if (user.disabled) {
        console.log('Calling enableUserInProject...');
        await dispatch(User.actions.enableUserInProject(user.id, projectId))
        toast.success('Success! User has been added to this project')
      } else {
        console.log('Calling removeUserFromProject...');
        await dispatch(User.actions.removeUserFromProject(user.id, projectId))
        toast.success('Success! User has been disabled for this project')
      }
      console.log('Success! Refreshing data...');
      // Refresh the user list
      await dispatch(User.actions.fetchProjectUsers(projectId));
    } catch (e) {
      console.error('=== Error removing user ===', e);
      toast.error(`Error: ${e.message || 'Unknown error'}`)
    }
  }

  const onCancelInvite = async (invite) => {
    try {
      await dispatch(User.actions.cancelInviteForProject(invite, projectId));
      toast.success('Invitation has been cancelled');
      // Refresh pending invites
      try {
        const invites = await dispatch(User.actions.fetchPendingInviteesForProject(projectId));
        setPendingInvites(invites || []);
      } catch (fetchError) {
        console.warn('Could not refresh pending invites:', fetchError);
      }
    } catch (e) {
      console.error('Error cancelling invitation:', e);
      toast.error(`Error cancelling invitation: ${e.message || 'Unknown error'}`);
    }
  }

  const onResendInvite = async (invite) => {
    try {
      // Prepare the invite body - include user_id if available
      const inviteBody = {
        email: invite.email,
        role: invite.role || UserRoleIds.VIEWER
      };
      
      // If the invitee is a registered user, include their user_id
      if (invite.id) {
        inviteBody.user_id = invite.id;
      }
      
      // Resend the invite
      await dispatch(User.actions.inviteUserToProject(inviteBody, projectId));
      
      toast.success('Invitation has been resent');
      // Refresh pending invites
      try {
        const invites = await dispatch(User.actions.fetchPendingInviteesForProject(projectId));
        setPendingInvites(invites || []);
      } catch (fetchError) {
        console.warn('Could not refresh pending invites:', fetchError);
      }
    } catch (e) {
      console.error('Error resending invitation:', e);
      toast.error(`Error resending invitation: ${e.message || 'Unknown error'}`);
    }
  }

  const inviteUserBtn = canInvite ? <Button
    title={"Invite"}
    onClick={onInviteUser}
    icon={<InviteIcon />}
    color={"green-outline"}
    size={"medium"}
  /> : null;

  if (loading) {
    return (
      <div className='MiniDashboardChatbot__reps-loading'>
        <Loader size='large' color='grey' />
      </div>
    );
  }

  if (error) {
    return (
      <div className={'MiniDashboardChatbot__reps-error'}>
        {error}
        <Button onClick={fetchTeamMembers}
          color={"green-outline"}
          title={"Retry"} />
      </div>
    );
  }

  return (
    <div className={"MiniDashboardChatbot__reps"}>
      <div className={"MiniDashboardChatbot__reps-section"}>
        <div className={"MiniDashboardChatbot__reps-header"}>
          {`Members in project (${activeUsersInProject.length})`}
          {inviteUserBtn}
        </div>

        <div className={"TeamMembers__List"}>
          <MemberItemsList users={activeUsersInProject}
            currentUser={currentUser}
            mode={MemberItemModes.MANAGE}
            onRemove={onRemoveUser}
            canManage={canInvite}
            allowedRoles={[UserRoleIds.PARTNER, UserRoleIds.REPRESENTATIVE]}
          />
        </div>
      </div>

      {canInvite && pendingInvites.length > 0 && (
        <div className={"MiniDashboardChatbot__reps-section"}>
          <div className={"MiniDashboardChatbot__reps-header"}>
            {`Pending (${pendingInvites.length})`}
          </div>

          <div className={"TeamMembers__List"}>
            <PendingInviteItemsList invites={pendingInvites}
              currentUser={currentUser}
              onResend={onResendInvite}
              onCancel={onCancelInvite}
              canManage={canInvite}
            />
          </div>
        </div>
      )}

      {canInvite && !_.isEmpty(disabledUsersInProject) && (
        <div className={"MiniDashboardChatbot__reps-section"}>
          <div className={"MiniDashboardChatbot__reps-header"}>
            {`Disabled (${disabledUsersInProject.length})`}
          </div>

          <div className={"TeamMembers__List"}>
            <MemberItemsList users={disabledUsersInProject}
              currentUser={currentUser}
              mode={MemberItemModes.MANAGE}
              onRemove={onRemoveUser}
              canManage={canInvite}
              allowedRoles={[UserRoleIds.PARTNER, UserRoleIds.REPRESENTATIVE]}
            />
          </div>
        </div>
      )}
    </div>
  )
}

